import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Settings2, Flag, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/TopBar";
import { Chronometer } from "@/components/Chronometer";
import { EventGrid } from "@/components/EventGrid";
import { EventModal } from "@/components/EventModal";
import { Timeline } from "@/components/Timeline";
import { Shootout } from "@/components/Shootout";
import { NotFound } from "@/components/NotFound";
import { getMatch, updateMatch, addEvent, deleteEvent, updateEvent } from "@/lib/api";
import { EditTargetModal } from "@/components/EditTargetModal";
import { currentSeconds, EVENT_MAP, eventMinute, scoreText, priorYellows, targetLabel, PERIOD_LABEL, isCup, AUTO_RED, linkedCards } from "@/lib/events";
import { PostMatch } from "@/components/PostMatch";
import { teamStyle } from "@/lib/colors";

const useTick = (active) => {
  const [, setT] = useState(0);
  useEffect(() => {
    if (!active) return;
    const h = setInterval(() => setT((t) => t + 1), 250);
    return () => clearInterval(h);
  }, [active]);
};

export default function Live() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [match, setMatch] = useState(null);
  const [pending, setPending] = useState(null);
  const [editing, setEditing] = useState(null);
  const savingRef = useRef(false);
  const queueRef = useRef(Promise.resolve());
  const running = !!match?.clock_started_at;
  const inInterval = !!match?.halftime_started_at;
  const preMatch = !!match && match.period === 1 && !match.clock_started_at && !match.clock_elapsed && !inInterval;
  useTick(running || inInterval);

  useEffect(() => { getMatch(id).then(setMatch).catch(() => setMatch(false)); }, [id]);
  if (match === false) return <NotFound />;
  if (!match) return null;

  const secs = currentSeconds(match);
  const intervalSecs = inInterval ? (Date.now() - Date.parse(match.halftime_started_at)) / 1000 : null;
  const update = async (data) => updateMatch(id, data).then(setMatch).catch(() => toast.error("Não foi possível guardar. Tente novamente."));
  const changeShootout = (shootout) => {
    setMatch((m) => ({ ...m, shootout }));
    queueRef.current = queueRef.current.then(() => updateMatch(id, { shootout })).catch(() => toast.error("Falha ao guardar penálti"));
  };

  const start = () => update({ clock_started_at: new Date().toISOString(), status: "live" });
  const pause = () => update({ clock_elapsed: currentSeconds(match), clock_started_at: null });
  const reset = () => window.confirm("Reiniciar o cronómetro para 00:00?") && update({
    clock_elapsed: 0, clock_started_at: running ? new Date().toISOString() : null, ...(match.period === 2 && { half2_reset: true }),
  });
  const endPeriod = () => window.confirm(`Terminar a ${PERIOD_LABEL[match.period]} e iniciar o intervalo${match.period === 2 ? " antes do prolongamento" : ""}?`) &&
    update({ clock_elapsed: currentSeconds(match), clock_started_at: null, halftime_started_at: new Date().toISOString() });
  const startNextPeriod = () => update({
    period: match.period + 1, ...(match.period === 1 && { half2_reset: true }), clock_elapsed: 0, clock_started_at: new Date().toISOString(),
    halftime_started_at: null, halftime_elapsed: intervalSecs,
  });
  const changePeriod = (p) => update(p === 2 ? { period: 2, half2_reset: currentSeconds(match) < 60 } : { period: p });
  const startShootout = () => window.confirm("Iniciar o desempate por penáltis? O cronómetro será parado.") &&
    update({ clock_elapsed: currentSeconds(match), clock_started_at: null, halftime_started_at: null, shootout_active: true });
  const finish = async () => {
    if (!window.confirm("Terminar o jogo?")) return;
    await updateMatch(id, { status: "finished", post_match: false, shootout_active: false });
    navigate(`/jogo/${id}/relatorio`);
  };
  const enterPostMatch = () => window.confirm("Fim do jogo? Ainda poderá registar cartões antes de terminar.") &&
    update({ clock_elapsed: currentSeconds(match), clock_started_at: null, halftime_started_at: null, shootout_active: false, post_match: true });
  const pickCard = (phase) => (type) => setPending({ type, seconds: Math.floor(currentSeconds(match)), period: match.period, phase });

  const saveEvent = async (data) => {
    if (savingRef.current) return;
    savingRef.current = true;
    const list = [].concat(data).flatMap((d) =>
      d.type === "amarelo" && priorYellows(match, d) > 0
        ? [d, { ...d, type: "vermelho", note: AUTO_RED }]
        : [d]);
    const saved = [];
    for (const d of list) {
      const ev = await addEvent(id, d).catch(() => null);
      if (ev) saved.push(ev);
    }
    savingRef.current = false;
    if (!saved.length) return;
    setMatch((m) => ({ ...m, events: [...m.events, ...saved] }));
    setPending(null);
    const ev = saved[0];
    if (saved.some((e) => e.note === AUTO_RED))
      toast.error(`2º amarelo a ${targetLabel(ev)} — Cartão Vermelho registado automaticamente`, { duration: 6000 });
    else toast.success(`${EVENT_MAP[ev.type].label}${saved.length > 1 ? ` ×${saved.length}` : ""} · ${eventMinute(ev, match.half2_reset)}`);
  };
  const undoLast = () => {
    const last = [...match.events].sort((a, b) => a.created_at.localeCompare(b.created_at)).pop();
    if (last) removeEvent(last.id, true);
  };
  const editTarget = async (target) => {
    const ev = editing;
    const linked = ev.type === "amarelo" ? linkedCards(match, ev) : [];
    const updated = [];
    for (const e of [ev, ...linked]) {
      const u = await updateEvent(id, e.id, target).catch(() => null);
      if (u) updated.push(u);
    }
    if (!updated.length) return;
    setMatch((m) => ({ ...m, events: m.events.map((e) => updated.find((u) => u.id === e.id) || e) }));
    setEditing(null);
    toast.success(`${EVENT_MAP[ev.type].label} atribuído a ${targetLabel(updated[0])} (${match[target.team].name})`);
  };
  const removeEvent = async (eid, undo = false) => {
    const ev = match.events.find((e) => e.id === eid);
    const later = ev.type === "substituicao" && match.events.some((e) => e.type === "substituicao" && e.team === ev.team && e.id !== eid
      && (e.period > ev.period || (e.period === ev.period && e.seconds >= ev.seconds)) && [e.target, e.player_in].includes(ev.player_in));
    if (later) return toast.error(`Elimine primeiro a substituição posterior que envolve o Nº ${ev.player_in}`);
    const ids = [eid, ...linkedCards(match, ev).map((e) => e.id)];
    for (const x of ids) if (!(await deleteEvent(id, x).catch(() => null))) return;
    setMatch((m) => ({ ...m, events: m.events.filter((e) => !ids.includes(e.id)) }));
    if (undo) toast(`Desfeito: ${ids.length > 1 ? "2º Amarelo + Cartão Vermelho" : EVENT_MAP[ev.type].label} · ${eventMinute(ev, match.half2_reset)}`);
    return true;
  };
  const annulRed = (ev) => {
    const auto = ev.note === AUTO_RED;
    if (!window.confirm(`Anular a expulsão de ${targetLabel(ev)} (${match[ev.team].name})? O cartão vermelho${auto ? " e o 2º amarelo serão apagados" : " será apagado"}.`)) return;
    removeEvent(ev.id).then((ok) => ok === true && toast.success(`Expulsão anulada · ${targetLabel(ev)} volta às opções`));
  };

  return (
    <div className="relative z-10">
      <TopBar>
        <Link to={`/jogo/${id}/configurar`} data-testid="edit-setup-btn" className="tap h-10 px-3 border border-white/15 flex items-center gap-2 text-sm hover:bg-white/5">
          <Settings2 size={16} /> <span className="hidden sm:inline">Editar plantel</span>
        </Link>
        <button data-testid="finish-match-btn" onClick={match.post_match ? finish : enterPostMatch} className="tap h-10 px-3 bg-white text-black flex items-center gap-2 text-sm font-semibold hover:bg-zinc-200">
          <Flag size={16} /> {match.post_match ? "Terminar" : "Fim do jogo"}
        </button>
      </TopBar>
      <main className="max-w-7xl mx-auto px-3 sm:px-8 py-4 sm:py-10 flex flex-col gap-4 lg:block lg:space-y-8">
        <div data-testid="scoreboard" className="self-start inline-flex max-w-full items-stretch font-display text-xl sm:text-4xl font-bold uppercase border border-white/20">
          <span data-testid="live-home-name" style={teamStyle(match.home)} className="truncate px-3 sm:px-6 py-1.5 sm:py-2">{match.home.name}</span>
          <span data-testid="live-score" className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#0A0A0A] text-white tabular-nums whitespace-nowrap">{scoreText(match)}</span>
          <span data-testid="live-away-name" style={teamStyle(match.away)} className="truncate px-3 sm:px-6 py-1.5 sm:py-2">{match.away.name}</span>
        </div>
        {match.post_match && <PostMatch onCard={pickCard("pos_jogo")} onBack={() => update({ post_match: false })} onFinish={finish} />}
        {match.shootout_active && !match.post_match && <Shootout match={match} onChange={changeShootout} onCard={pickCard("penaltis")}
          onBack={() => update({ shootout_active: false })} />}
        <div className={match.shootout_active || match.post_match ? "hidden" : "contents lg:grid lg:grid-cols-[0.85fr_1.4fr] lg:gap-8"}>
          <div className="contents lg:flex lg:flex-col lg:gap-4">
            <Chronometer seconds={secs} running={running} period={match.period} reset={match.half2_reset}
              interval={intervalSecs} onEndHalf={endPeriod} onStartSecond={startNextPeriod} onShootout={startShootout} cup={isCup(match)}
              onStart={start} onPause={pause} onReset={reset} onPeriod={(p) => p !== match.period && changePeriod(p)} />
            <button data-testid="undo-last-btn" onClick={undoLast} disabled={!match.events.length}
              className="tap w-full h-12 lg:h-16 border border-[#FF3B30]/60 text-[#FF3B30] font-display text-lg lg:text-xl font-bold uppercase flex items-center justify-center gap-3 hover:bg-[#FF3B30] hover:text-white disabled:opacity-30 disabled:pointer-events-none">
              <Undo2 size={20} /> Desfazer último
            </button>
          </div>
          <EventGrid match={match} disabled={!running && !inInterval && !preMatch}
            onlyKeys={inInterval ? ["substituicao", "amarelo", "vermelho"] : preMatch ? ["outros"] : null}
            hint={inInterval ? "Intervalo: substituições e cartões" : "Antes do início: só notas em Outros"}
            onPick={(type) => {
              const ev = inInterval ? { type, seconds: 0, period: match.period + 1, halftime: true }
                : preMatch ? { type, seconds: 0, period: 1, phase: "pre_jogo" } : { type, seconds: Math.floor(currentSeconds(match)), period: match.period };
              EVENT_MAP[type].flow === "direct" ? saveEvent(ev) : setPending(ev);
            }} />
        </div>
        <section>
          <div className="flex items-baseline justify-between mb-3">
            <h2 className="font-display text-2xl font-bold uppercase">Registo temporal</h2>
            <span data-testid="event-total" className="label-caps">{match.events.length} eventos</span>
          </div>
          <Timeline match={match} onDelete={removeEvent} onEdit={setEditing} onAnnul={annulRed} />
        </section>
      </main>
      {pending && <EventModal pending={pending} match={match} onCancel={() => setPending(null)} onSave={saveEvent} />}
      {editing && <EditTargetModal event={editing} match={match} onCancel={() => setEditing(null)} onSave={editTarget} />}
    </div>
  );
}
